<template>
  <div class="automated-email-templates-ui">
    <div class="automated-email-templates-ui__header">
      <VHeading :level="3" class="auto-email-header">
        Email Templates · {{ isLettings ? 'Lettings' : 'Sales' }}
      </VHeading>
      <VHeading :level="4" class="auto-email-subheader">
        Manage the email templates used for automatic emails. You can turn each template on or off.
        Templates are grouped by type.
      </VHeading>
    </div>

    <div v-for="trigger in triggers" :key="trigger.id" class="automated-email-templates-ui__group">
      <div class="automated-email-templates-ui__group-header">
        <VHeading :level="4" class="auto-email-trigger">{{ trigger.name }}</VHeading>
        <span class="automated-email-templates-ui__group-count">
          {{ getLinkedTemplatesCount(trigger) }} active
        </span>
      </div>

      <div class="automated-email-templates-ui__grid">
        <div
          v-for="template in trigger.templates"
          :key="template.id"
          class="automated-email-templates-ui__card"
        >
          <div class="automated-email-templates-ui__card-header">
            <p class="automated-email-templates-ui__card-title">{{ template.title }}</p>
            <span class="automated-email-templates-ui__card-switch">
              <VTooltip v-if="template.is_linked && isLastLinkedTemplate(template.id)" class="auto-email-tooltip">
                <template #activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <div class="automated-email-templates-ui__card-switch--disabled">
                      <VSwitch :model-value="true" class="black" />
                    </div>
                  </span>
                </template>
                At least one template must stay active
              </VTooltip>
              <VSwitch
                v-else
                :model-value="template.is_linked"
                class="black"
                @update:model-value="
                  (value: boolean) => emit('toggle-template', trigger.id, template.id, value)
                "
              />
            </span>
          </div>

          <VDivider class="no-margin" />
          <div class="automated-email-templates-ui__card-actions">
            <VBtn class="auto-email-template" @click="openPreview(trigger, template)">Preview</VBtn>
            <VBtn class="auto-email-template" @click="openEdit(trigger, template)">Edit</VBtn>
          </div>
        </div>
      </div>
    </div>
    <div class="automated-email-templates-ui__actions">
      <VBtn :loading="isSaving" @click="emit('save')">Save</VBtn>
    </div>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { PropType } from 'vue';
  import type { TTrigger, TEmailTemplate } from '~/modules/opportunities/automated-email/types';

  // Composables
  import useModal from '~/composables/use-modal';

  // Components
  import VHeading from '~/misc/v-heading/VHeading.vue';
  import VTooltip from '~/misc/v-tooltip/VTooltip.vue';
  import VSwitch from '~/misc/v-switch';
  import VBtn from '~/misc/v-btn';
  import VDivider from '~/misc/v-divider/VDivider.vue';

  const TemplatePreviewModal = defineAsyncComponent(
    () =>
      import(
        '~/modules/opportunities/automated-email/settings/templates/preview-modal/TemplatePreviewModal.vue'
      )
  );

  const TemplateEditModal = defineAsyncComponent(
    () =>
      import(
        '~/modules/opportunities/automated-email/settings/templates/edit-modal/TemplateEditModal.vue'
      )
  );

  const props = defineProps({
    triggers: {
      type: Array as PropType<TTrigger[]>,
      required: true,
    },
    isLettings: {
      type: Boolean,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    isLastLinkedTemplate: {
      type: Function as PropType<(templateId: number) => boolean>,
      required: true,
    },
    persistTemplateEdit: {
      type: Function as PropType<
        (triggerId: number, templateId: number, title: string, content: string) => Promise<void>
      >,
      required: true,
    },
  });

  const emit = defineEmits<{
    (e: 'toggle-template', triggerId: number, templateId: number, value: boolean): void;
    (e: 'save'): void;
  }>();

  const { show: showModal } = useModal();

  const openEdit = (trigger: TTrigger, template: TEmailTemplate): void => {
    showModal(TemplateEditModal, {
      title: template.title,
      content: template.content,
      triggerName: trigger.name,
      saveCallback: async (newTitle: string, newContent: string) => {
        await props.persistTemplateEdit(trigger.id, template.id, newTitle, newContent);
      },
    });
  };

  const openPreview = (trigger: TTrigger, template: TEmailTemplate): void => {
    showModal(TemplatePreviewModal, {
      title: template.title,
      content: template.content,
      triggerName: trigger.name,
      editCallback: () => openEdit(trigger, template),
    });
  };

  const getLinkedTemplatesCount = (trigger: TTrigger): number =>
    trigger.templates.filter((t: TEmailTemplate): boolean => t.is_linked).length;
</script>
